import React, { useEffect, useRef } from 'react';
import { Icon } from '@repo/ui';
import { documentApi } from '../api';

interface DownloadMenuProps {
  transaction: any;
  isOpen: boolean;
  onClose: () => void;
  onViewPdf: (type: 'invoice' | 'ledger') => void;
  onViewReceipt: (filename: string) => void;
  onDownloadReceiptsZip?: () => void;
  position: { x: number; y: number };
}

export function DownloadMenu({
  transaction,
  isOpen,
  onClose,
  onViewPdf,
  onViewReceipt,
  onDownloadReceiptsZip,
  position,
}: DownloadMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !transaction) return null;

  // Determine what options to show based on Trust Request status
  // NOTE: We now show PDF viewer options for stored S3 documents

  // Helper function to get trust request status (aligned with JuniperTransactions)
  const getTrustRequestStatus = (transaction: any) => {
    // Check multiple possible locations for the trust request status
    return (
      transaction.trustRequest?.state ||
      transaction.trustRequest?.trrStatus ||
      transaction.liveData?.trustRequestBill?.state ||
      transaction.trustRequestState ||
      null
    );
  };

  const trustRequestStatus = getTrustRequestStatus(transaction);
  const isAwaitingPaymentOrPaid =
    trustRequestStatus === 'awaiting_payment' || trustRequestStatus === 'paid';

  // Show PDF viewer options when Trust Request is awaiting_payment OR paid
  const showPdfOptions = isAwaitingPaymentOrPaid;

  // Get stored documents from S3
  const storedDocs = documentApi.getStoredDocuments(transaction);
  const hasInvoice = !!storedDocs.invoice;
  const hasLedger = !!storedDocs.ledger;
  const hasReceipts = storedDocs.receipts.length > 0;

  // Adjust menu position to stay within viewport with proper padding
  const adjustedPosition = { ...position };
  if (typeof window !== 'undefined') {
    const menuWidth = 280;
    const menuHeight = 400; // Increased estimate to account for all sections
    const padding = 20; // Minimum distance from viewport edges
    const triggerHeight = 40; // Estimated height of the trigger button

    // Adjust horizontal position
    if (position.x + menuWidth + padding > window.innerWidth) {
      adjustedPosition.x = Math.max(padding, window.innerWidth - menuWidth - padding);
    } else if (position.x < padding) {
      adjustedPosition.x = padding;
    }

    // Adjust vertical position - be more aggressive about positioning above
    const spaceBelow = window.innerHeight - position.y - padding;
    const spaceAbove = position.y - padding;

    if (menuHeight > spaceBelow && spaceAbove > spaceBelow) {
      // Position above the trigger if there's more space above
      adjustedPosition.y = Math.max(padding, position.y - menuHeight - triggerHeight);
    } else if (position.y + menuHeight + padding > window.innerHeight) {
      // Position at the bottom with padding if it would go off screen
      adjustedPosition.y = Math.max(padding, window.innerHeight - menuHeight - padding);
    } else if (position.y < padding) {
      adjustedPosition.y = padding;
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" />

      {/* Menu */}
      <div
        ref={menuRef}
        className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 min-w-[280px] max-w-sm max-h-[80vh] overflow-y-auto"
        style={{
          left: adjustedPosition.x,
          top: adjustedPosition.y,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Download Options
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Icon name="HiX" className="h-4 w-4" />
          </button>
        </div>

        {/* Transaction Info */}
        <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
          Invoice: {transaction.operationalBill?.invoiceNumber || 'N/A'}
        </div>

        {/* Status Message when PDFs are not available */}
        {!isAwaitingPaymentOrPaid && (
          <div className="py-4 px-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                PDFs Not Available
              </div>
              <div className="text-xs text-yellow-700 dark:text-yellow-300">
                Trust Request must be in 'awaiting_payment' or 'paid' status to view PDFs.
                <br />
                Current status:{' '}
                <span className="font-medium">{trustRequestStatus || 'unknown'}</span>
              </div>
            </div>
          </div>
        )}

        {/* PDF Viewer Section */}
        {showPdfOptions && (
          <div className="py-2">
            <div className="px-4 py-1">
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                View Invoice
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Open PDF viewer with download option
              </p>
            </div>

            {hasInvoice && (
              <button
                onClick={() => {
                  onViewPdf('invoice');
                  onClose();
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Icon name="HiEye" className="h-4 w-4 mr-3 text-xtnd-blue" />
                <div className="flex-1 text-left">
                  <div className="font-medium">View Invoice PDF</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {documentApi.formatFileSize(storedDocs.invoice!.size)}
                  </div>
                </div>
              </button>
            )}

            {hasLedger && (
              <button
                onClick={() => {
                  onViewPdf('ledger');
                  onClose();
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Icon name="HiEye" className="h-4 w-4 mr-3 text-xtnd-blue" />
                <div className="flex-1 text-left">
                  <div className="font-medium">View Ledger PDF</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {documentApi.formatFileSize(storedDocs.ledger!.size)}
                  </div>
                </div>
              </button>
            )}

            {!hasInvoice && !hasLedger && (
              <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 italic">
                No PDFs stored yet
              </div>
            )}
          </div>
        )}

        {/* Receipts Section */}
        {showPdfOptions && hasReceipts && (
          <div className="py-2 border-t border-gray-100 dark:border-gray-700">
            <div className="px-4 py-1 flex items-center justify-between">
              <div>
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Receipts ({storedDocs.receipts.length})
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Attached documents from Clio
                </p>
              </div>

              {/* ZIP Download Button */}
              {onDownloadReceiptsZip && (
                <button
                  onClick={() => {
                    onDownloadReceiptsZip();
                    onClose();
                  }}
                  className="flex items-center px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors"
                  title="Download all receipts as ZIP"
                >
                  <Icon name="HiDownload" className="h-3 w-3 mr-1" />
                  ZIP
                </button>
              )}
            </div>

            <div className="max-h-48 overflow-y-auto">
              {storedDocs.receipts.map((receipt, index) => (
                <button
                  key={receipt.clioDocumentId}
                  onClick={() => {
                    onViewReceipt(receipt.filename);
                    onClose();
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="HiDocumentText"
                    className="h-4 w-4 mr-3 text-green-500 flex-shrink-0"
                  />
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium truncate">{receipt.filename}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {documentApi.formatFileSize(receipt.size)}
                      {receipt.willBeAttached && (
                        <span className="ml-2 text-green-600 dark:text-green-400">
                          • Will attach
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer note */}
        <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700">
          Press and hold download button to show this menu
        </div>
      </div>
    </>
  );
}
